'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

/** ~8 lines of text-sm; tall pastes collapse like Claude / ChatGPT. */
const DEFAULT_COLLAPSED_MAX_HEIGHT_PX = 160;

interface CollapsibleUserMessageProps {
  content: string;
  collapsedMaxHeight?: number;
  /** Tailwind `from-*` class for the bottom fade when collapsed. */
  fadeFromClass?: string;
  /** Classes for the Show more / Show less control. */
  toggleClassName?: string;
  className?: string;
}

export function CollapsibleUserMessage({
  content,
  collapsedMaxHeight = DEFAULT_COLLAPSED_MAX_HEIGHT_PX,
  fadeFromClass = 'from-primary',
  toggleClassName = 'text-white/70 hover:text-white',
  className,
}: CollapsibleUserMessageProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);

  useLayoutEffect(() => {
    setExpanded(false);
  }, [content]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;

    const measure = () => {
      const height = el.scrollHeight;
      setFullHeight(height);
      setNeedsCollapse(height > collapsedMaxHeight + 4);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [content, collapsedMaxHeight]);

  const isClamped = needsCollapse && !expanded;
  const targetHeight =
    !needsCollapse || expanded
      ? fullHeight > 0
        ? fullHeight
        : 'auto'
      : collapsedMaxHeight;

  return (
    <div className={cn(className)}>
      <div className="relative">
        <motion.div
          initial={false}
          animate={{ height: targetHeight }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <div
            ref={innerRef}
            className="whitespace-pre-wrap break-words overflow-wrap-anywhere leading-relaxed"
          >
            {content}
          </div>
        </motion.div>

        <AnimatePresence>
          {isClamped ? (
            <motion.div
              key="fade"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t to-transparent',
                fadeFromClass,
              )}
              aria-hidden
            />
          ) : null}
        </AnimatePresence>
      </div>

      {needsCollapse ? (
        <motion.button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={cn(
            'mt-1.5 inline-flex items-center gap-0.5 text-[13px] font-medium transition-colors',
            toggleClassName,
          )}
          whileTap={{ scale: 0.97 }}
        >
          {expanded ? 'Show less' : 'Show more'}
          <motion.span
            initial={false}
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex"
          >
            <ChevronDown className="size-3.5 shrink-0 opacity-80" aria-hidden />
          </motion.span>
        </motion.button>
      ) : null}
    </div>
  );
}
